import { useState } from 'react';
import { Trans } from 'react-i18next';

import { actions, invoiceStatusConfig } from 'entities';

import { Collapse, Button, LabeledTextarea, Column, Row } from 'components/Blocks';
import { MonetaryInput } from 'components/Inputs';
import { UploadFileDropzone } from 'components/Uploads';

import { CircleXIcon, FileCheckIcon } from 'components/Icons';

import { InvoiceDate } from './Date';

import { useFormControl } from 'hooks';
import { useUser } from 'hooks/user';
import { useLang } from 'hooks/lang';

import { omit, omitUndefined } from 'utils/object';
import { getIsoDatePart } from 'utils/date';
import { getFirst } from 'utils/array';

import { InvoiceTabProps } from './types';
import { UploadedFile } from 'types';

export function ConfirmPayment({
  invoice,
  overwrite,
  changeTab,
  closeModal,
}: InvoiceTabProps): JSX.Element {
  const user = useUser();

  const [invoiceFiles, setInvoiceFiles] = useState<UploadedFile[]>([]);

  const { state, dispatch, handleChange } = useFormControl({
    date: null as null | string,
    valuePaid: invoice.netValue as string,
    balanceComment: '',
  });

  const paidValue = Number(state.valuePaid);
  const netValue = Number(invoice.netValue);

  const mismatchedValue = paidValue !== netValue;
  const difference = paidValue - netValue;

  const { t } = useLang();

  const uploadBlock = [
    !invoiceFiles.length,
    paidValue === 0 ? netValue !== 0 : false,
    !Object.values(omit(state, ['balanceComment'])).every(Boolean),
  ].some(Boolean);

  const uploadInvoice = actions.invoice.confirmInvoicePayment.use({
    block: uploadBlock,

    params: omitUndefined({
      invoice: invoice.id,
      valuePaid: state.valuePaid,
      date: state.date ? getIsoDatePart(state.date) : '',
      file: getFirst(invoiceFiles),

      ...(mismatchedValue && state.balanceComment.trim()
        ? { balanceComment: state.balanceComment }
        : {}),
    }),

    onSuccess: () => {
      overwrite((old) => ({
        ...old,

        status: invoiceStatusConfig.paid.code,

        valuePaid: state.valuePaid,
        paidAt: getIsoDatePart(state.date!),
        paymentFile: getFirst(invoiceFiles),

        ...(mismatchedValue && state.balanceComment.trim()
          ? { balanceComment: state.balanceComment }
          : {}),

        files: [
          ...old.files,
          {
            ...getFirst(invoiceFiles),
            type: 'PAYMENT_FILE',
            invoice: invoice.id,

            ...(mismatchedValue && state.balanceComment.trim()
              ? { balanceComment: state.balanceComment }
              : {}),

            uploadedBy: {
              ...user,
              team: user.team.id,
            },
          },
        ],
      }));

      changeTab('data');
    },
  });

  return (
    <>
      <Column gap="8">
        <h6 className="text-center font-semibold">{t('upload-invoice')}</h6>

        <Column gap="6">
          <Row className="phone:flex-col phone:items-stretch" justifyContent="space-between">
            <MonetaryInput
              label={t('net-value') as string}
              disabled
              value={invoice.netValue}
              currency={invoice.currency}
              containerClassName="w-[80%] phone:w-full"
            />

            <MonetaryInput
              label={t('paid-value') as string}
              onChange={handleChange}
              currency={invoice.currency}
              containerClassName="w-[80%] phone:w-full"
              currencyDisabled
              value={state.valuePaid}
              name="valuePaid"
            />
          </Row>

          <Collapse expanded={mismatchedValue}>
            <p className="mb-4 text-center text-sm italic text-destructive-light">
              <Trans i18nKey="this-will-generate-a-balance-entry-of">
                {{ balance: `${difference.toFixed(2)} ${invoice.currency}` }}
              </Trans>
            </p>

            <LabeledTextarea
              label={t('add-balance-comment?') as string}
              placeholder={t('if-necessary-balance-placeholder') as string}
              value={state.balanceComment}
              name="balanceComment"
              onChange={handleChange}
            />
          </Collapse>

          <InvoiceDate
            state={state}
            dispatch={dispatch}
            helperText={t('payment-date-helper')}
            label={t('payment-date')}
          />

          <UploadFileDropzone
            files={invoiceFiles}
            setFiles={setInvoiceFiles}
            uploadPrefix={`invoices/${invoice.id}` as 'invoiceId'}
            multiple={false}
            showInputOnDisabled={false}
            acceptedFormats={[
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'application/pdf',
              'image/*',
            ]}
          />
        </Column>
      </Column>

      <Row justifyContent="space-between">
        <Button variant="outlined" onClick={closeModal}>
          <CircleXIcon size={14} />
          {t('cancel')}
        </Button>

        <Button disabled={uploadBlock} {...uploadInvoice.buttonProps}>
          {t('register-payment')}
          <FileCheckIcon />
        </Button>
      </Row>
    </>
  );
}
